/**
 * The contact inspector.
 *
 * Built around the one thing the model insists on: each address carries its own
 * consent, its own source, and its own history. There is no control here that
 * changes consent for the whole person, because that is not something the
 * person ever did.
 */

import { useEffect, useState, type FormEvent } from "react";

import { api, ApiError, download } from "../api/client";
import { useAsync } from "../hooks";
import { Consent, ConsentGlyph, SOURCE_LABEL, actorLabel, isSendable } from "../components/Consent";
import { CopyValue } from "../components/CopyValue";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type {
  Channel,
  Contact,
  ContactTag,
  ConsentEvent,
  ConsentStatus,
  CursorPage,
  CustomField,
  Group,
  Identity,
} from "../api/types";

interface DrawerProps {
  contactId: string;
  onClose: () => void;
  onChanged: () => void;
}

export function ContactDrawer({ contactId, onClose, onChanged }: DrawerProps) {
  const { can } = useSession();

  const contact = useAsync<Contact>(
    (signal) => api.get<Contact>(`/v1/contacts/${encodeURIComponent(contactId)}`, { signal }),
    [contactId],
  );

  const fields = useAsync<CustomField[]>(
    (signal) => api.get<CustomField[]>("/v1/custom-fields", { signal }),
    [],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const data = contact.data;

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} />
      <aside className="oc-drawer" role="dialog" aria-modal="true" aria-labelledby="contact-title">
        <header className="oc-drawer__header">
          <div style={{ minWidth: 0 }}>
            <div className="oc-label" style={{ marginBottom: 8 }}>
              Contact
            </div>
            <h2 className="oc-headline-md" id="contact-title" style={{ marginBottom: 8 }}>
              {data?.name || (contact.loading ? "Loading…" : "No name")}
            </h2>
            <CopyValue value={contactId} label="Copy contact id" />
          </div>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" className="oc-nav__icon" />
          </button>
        </header>

        <div className="oc-drawer__body">
          {contact.error && <p className="field-error">{contact.error.message}</p>}

          {data && (
            <>
              <Identities
                contact={data}
                editable={can("contacts:write")}
                onChanged={() => {
                  contact.reload();
                  onChanged();
                }}
              />
              <Details
                contact={data}
                fields={fields.data ?? []}
                editable={can("contacts:write")}
                onChanged={() => {
                  contact.reload();
                  onChanged();
                }}
              />
              <TagsSection
                contact={data}
                editable={can("contacts:write")}
                onChanged={() => {
                  contact.reload();
                  onChanged();
                }}
              />
              <GroupsSection
                contact={data}
                editable={can("contacts:write")}
                onChanged={() => {
                  contact.reload();
                  onChanged();
                }}
              />
              <SubjectAccess contact={data} />
              <DangerZone contact={data} onErased={onChanged} onClose={onClose} />
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Identities({
  contact,
  editable,
  onChanged,
}: {
  contact: Contact;
  editable: boolean;
  onChanged: () => void;
}) {
  return (
    <section className="drawer-section">
      <span className="oc-label">Addresses and consent</span>

      {contact.identities.length === 0 && (
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          No addresses. There is no way to reach this contact.
        </p>
      )}

      {contact.identities.map((identity) => (
        <IdentityCard key={identity.id} identity={identity} editable={editable} onChanged={onChanged} />
      ))}

      {editable && <AddIdentity contact={contact} onChanged={onChanged} />}
    </section>
  );
}

/**
 * Attaching an address, which completes the consent model rather than adding a
 * convenience.
 *
 * Consent belongs to an identity — a stated invariant, and the reason there is
 * no operation that unsubscribes a person. Until now an identity could only be
 * created by a CSV import or the API, so the dashboard could move consent on an
 * address it was given and never obtain one.
 *
 * NEW ADDRESSES START AT `unknown`, not subscribed. Somebody typing an address
 * into a form on our side is the organization asserting reachability, not the
 * address owner agreeing to anything — the same distinction the importer draws,
 * and the reason a confirmed double opt-in is the only path that may move an
 * identity out of `unsubscribed`. Consent is then set deliberately on the card,
 * where it is recorded as `manual` and says so.
 */
function AddIdentity({ contact, onChanged }: { contact: Contact; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<Channel>("email");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`/v1/contacts/${encodeURIComponent(contact.id)}/identities`, {
        channel,
        address: address.trim(),
        consent_status: "unknown",
      });
      setOpen(false);
      setAddress("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} />
        Add an address
      </button>
    );
  }

  const fieldError = error instanceof ApiError ? error.fields : {};

  return (
    <form className="oc-panel" onSubmit={(event) => void submit(event)}>
      <div className="oc-panel__body stack" style={{ gap: "var(--oc-space-2)" }}>
        <div className="row" style={{ gap: "var(--oc-space-2)", alignItems: "flex-start" }}>
          <label className="login__field" style={{ flex: "none", width: 120 }}>
            <span className="oc-body-sm oc-text-secondary">Channel</span>
            <select
              className="oc-input oc-input--sm"
              value={channel}
              onChange={(event) => setChannel(event.target.value as Channel)}
            >
              {/* Only email is deliverable in V1, which the spec states. The
                  others are real in the model — consent, identities and
                  suppression are all per channel — so storing one is honest;
                  sending to it is what does not exist yet. */}
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </label>
          <label className="login__field grow">
            <span className="oc-body-sm oc-text-secondary">Address</span>
            <input
              className="oc-input oc-input--sm"
              value={address}
              placeholder={channel === "email" ? "someone@example.com" : "+15551234567"}
              aria-invalid={Boolean(fieldError["/address"])}
              onChange={(event) => setAddress(event.target.value)}
            />
          </label>
        </div>

        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          Added with no consent recorded, so campaigns skip it. Set consent on the address once
          you know what the person actually agreed to.
        </p>

        {error && <p className="field-error">{error.message}</p>}

        <div className="row" style={{ gap: "var(--oc-space-2)" }}>
          <button type="submit" className="oc-btn oc-btn--sm" disabled={saving || !address.trim()}>
            {saving ? "Adding…" : "Add address"}
          </button>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function IdentityCard({
  identity,
  editable,
  onChanged,
}: {
  identity: Identity;
  editable: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const removeIdentity = async () => {
    // Names the consent history, because that is the part somebody would not
    // expect to lose. `consent_events` is append-only precisely so a decision
    // cannot be quietly rewritten — and removing the identity it belongs to
    // takes it with them, which is the one way it can legitimately go.
    const ok = window.confirm(
      `Remove ${identity.address} from this contact?\n\n` +
        `Its consent history goes too — every recorded decision for this address, ` +
        `including how and when it was obtained. That record cannot be rebuilt.\n\n` +
        `Messages already sent to it are unaffected.`,
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/identities/${encodeURIComponent(identity.id)}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the address.");
      setBusy(false);
    }
  };

  const setConsent = async (status: ConsentStatus) => {
    setBusy(true);
    setError(null);
    try {
      // Recorded as a manual decision, because that is what it is: someone on
      // the team changed it. Claiming it came from a form would put a false
      // statement in the record that exists to be trusted.
      await api.put(`/v1/identities/${encodeURIComponent(identity.id)}/consent`, {
        status,
        source: "manual",
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change consent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oc-panel" style={{ background: "var(--oc-bg-inset)" }}>
      <div className="oc-panel__header">
        <div style={{ minWidth: 0 }}>
          <div className="oc-label" style={{ marginBottom: 4 }}>
            {identity.channel}
          </div>
          <CopyValue value={identity.address} className="oc-truncate" />
        </div>
        <Consent status={identity.consent_status} />
      </div>

      <div className="oc-panel__body stack" style={{ gap: "var(--oc-space-2)" }}>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          {identity.consent_source
            ? `Recorded via ${SOURCE_LABEL[identity.consent_source] ?? identity.consent_source}`
            : "Nothing was ever recorded for this address."}
          {identity.consent_at && ` on ${new Date(identity.consent_at).toLocaleDateString()}`}
        </p>

        {!isSendable(identity.consent_status) && (
          <p className="oc-body-sm oc-text-warning" style={{ margin: 0 }}>
            Campaigns skip this address.
          </p>
        )}

        {error && <p className="field-error">{error}</p>}

        <div className="row" style={{ gap: "var(--oc-space-2)", flexWrap: "wrap" }}>
          {editable && identity.consent_status !== "subscribed" && (
            <button type="button" className="oc-btn oc-btn--sm" disabled={busy} onClick={() => void setConsent("subscribed")}>
              Mark subscribed
            </button>
          )}
          {editable && identity.consent_status !== "unsubscribed" && (
            <button
              type="button"
              className="oc-btn oc-btn--danger oc-btn--sm"
              disabled={busy}
              onClick={() => void setConsent("unsubscribed")}
            >
              Unsubscribe
            </button>
          )}
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? "Hide history" : "History"}
          </button>
          {editable && (
            <button
              type="button"
              className="oc-btn oc-btn--ghost oc-btn--sm"
              disabled={busy}
              onClick={() => void removeIdentity()}
            >
              Remove
            </button>
          )}
        </div>

        {showHistory && <ConsentHistory identityId={identity.id} />}
      </div>
    </div>
  );
}

/**
 * The append-only record.
 *
 * Worth showing in full: current state answers "may I send today", and this
 * answers the question that gets asked after a complaint, which is a different
 * one.
 */
function ConsentHistory({ identityId }: { identityId: string }) {
  const { data, error, loading } = useAsync<ConsentEvent[]>(
    (signal) => api.get<ConsentEvent[]>(`/v1/identities/${encodeURIComponent(identityId)}/consent`, { signal }),
    [identityId],
  );

  if (loading) return <p className="oc-body-sm oc-text-secondary">Loading…</p>;
  if (error) return <p className="field-error">{error.message}</p>;
  if (!data || data.length === 0) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        No decisions recorded. This address arrived with nothing said about it.
      </p>
    );
  }

  return (
    <div className="oc-timeline" style={{ marginTop: "var(--oc-space-2)" }}>
      {data.map((event) => (
        <div className="oc-timeline__item" key={event.id}>
          <span className="oc-timeline__node">
            <ConsentGlyph status={event.status} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="oc-timeline__label">
              {event.previous_status ? `${event.previous_status} → ${event.status}` : event.status}
            </div>
            <div className="oc-timeline__time">
              {new Date(event.occurred_at).toLocaleString()}
              {event.source && ` · via ${SOURCE_LABEL[event.source] ?? event.source}`}
            </div>
            {actorLabel(event.actor) && (
              <div className="oc-body-sm oc-text-secondary">by {actorLabel(event.actor)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The contact's own details, and the first place this drawer could change them.
 *
 * `PATCH /v1/contacts/{id}` shipped with no caller anywhere in the dashboard, so
 * a contact could be imported and then never edited from the product — not a
 * misspelled name, not a timezone, not an attribute.
 */
function Details({
  contact,
  fields,
  editable,
  onChanged,
}: {
  contact: Contact;
  fields: CustomField[];
  editable: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <DetailsForm
        contact={contact}
        fields={fields}
        onDone={() => {
          setEditing(false);
          onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  // Read view keeps the old behaviour: only what is set. An empty row is noise
  // when you are looking rather than editing.
  const entries = fields
    .map((field) => ({ field, value: contact.attributes[field.key] }))
    .filter(({ value }) => value !== undefined && value !== null && value !== "");

  return (
    <section className="drawer-section">
      <div className="row row--between">
        <span className="oc-label">Details</span>
        {editable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
        )}
      </div>

      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px var(--oc-space-3)", margin: 0 }}>
        <dt className="oc-body-sm oc-text-secondary">Name</dt>
        <dd className="oc-body-sm" style={{ margin: 0, minWidth: 0 }}>
          {contact.name || <span className="oc-text-muted">Not set</span>}
        </dd>

        {contact.timezone && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Timezone</dt>
            <dd className="oc-body-sm" style={{ margin: 0 }}>{contact.timezone}</dd>
          </>
        )}
        {contact.language && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Language</dt>
            <dd className="oc-body-sm" style={{ margin: 0 }}>{contact.language}</dd>
          </>
        )}

        {entries.map(({ field, value }) => (
          <div key={field.id} style={{ display: "contents" }}>
            <dt className="oc-body-sm oc-text-secondary">{field.label}</dt>
            <dd className="oc-body-sm" style={{ margin: 0, minWidth: 0 }}>
              {String(value)}
            </dd>
          </div>
        ))}
      </dl>

      {fields.length === 0 && editable && (
        // Said here because the alternative is an Edit button that opens a form
        // with nothing in it but a name, and no explanation of why.
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          No custom fields are defined, so a contact can carry a name and addresses only. An
          administrator can define them under Settings → Custom fields.
        </p>
      )}
    </section>
  );
}

function DetailsForm({
  contact,
  fields,
  onDone,
  onCancel,
}: {
  contact: Contact;
  fields: CustomField[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(contact.name ?? "");
  const [timezone, setTimezone] = useState(contact.timezone ?? "");
  const [language, setLanguage] = useState(contact.language ?? "");
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of fields) {
      const v = contact.attributes[field.key];
      initial[field.key] = v === undefined || v === null ? "" : String(v);
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  // Values stored under a key with no definition any more.
  //
  // Deleting a definition deliberately leaves stored values in place — the API
  // says so, and recreating the field brings them back unchanged. But
  // attributes are replaced wholesale on write and validateAttributes refuses
  // an undefined key, so once one of these exists it can be neither preserved
  // nor sent: including it is a 422, omitting it deletes it. Saying so is the
  // only honest option, and it is why attributes are omitted from the request
  // entirely unless something actually changed.
  const defined = new Set(fields.map((f) => f.key));
  const orphaned = Object.keys(contact.attributes).filter((k) => !defined.has(k));

  const attributesChanged = fields.some((field) => {
    const before = contact.attributes[field.key];
    const beforeText = before === undefined || before === null ? "" : String(before);
    return (values[field.key] ?? "") !== beforeText;
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        timezone: timezone.trim(),
        language: language.trim(),
      };

      // Only when something actually changed. Update coalesces a null, so
      // omitting the key leaves stored attributes alone — which is what keeps
      // an orphaned value alive through an edit that never touched attributes.
      if (attributesChanged) {
        const attributes: Record<string, unknown> = {};
        for (const field of fields) {
          const raw = (values[field.key] ?? "").trim();
          if (raw === "") continue; // absent rather than null: both clear it
          attributes[field.key] = coerce(field, raw);
        }
        body.attributes = attributes;
      }

      await api.patch(`/v1/contacts/${encodeURIComponent(contact.id)}`, body);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  };

  const fieldError = error instanceof ApiError ? error.fields : {};

  return (
    <form className="drawer-section" onSubmit={(event) => void submit(event)}>
      <span className="oc-label">Details</span>

      <label className="login__field">
        <span className="oc-body-sm oc-text-secondary">Name</span>
        <input
          className="oc-input oc-input--sm"
          value={name}
          aria-invalid={Boolean(fieldError["/name"])}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <div className="row" style={{ gap: "var(--oc-space-3)", alignItems: "flex-start" }}>
        <label className="login__field grow">
          <span className="oc-body-sm oc-text-secondary">Timezone</span>
          <input
            className="oc-input oc-input--sm"
            value={timezone}
            placeholder="Europe/Berlin"
            aria-invalid={Boolean(fieldError["/timezone"])}
            onChange={(event) => setTimezone(event.target.value)}
          />
        </label>
        <label className="login__field grow">
          <span className="oc-body-sm oc-text-secondary">Language</span>
          <input
            className="oc-input oc-input--sm"
            value={language}
            placeholder="en"
            aria-invalid={Boolean(fieldError["/language"])}
            onChange={(event) => setLanguage(event.target.value)}
          />
        </label>
      </div>

      {/* Every defined field, set or not. The read view shows only what is
          filled in; an editor that did the same would make an empty field
          unreachable, which is the one thing an editor must not do. */}
      {fields.map((field) => (
        <AttributeInput
          key={field.id}
          field={field}
          value={values[field.key] ?? ""}
          invalid={Boolean(fieldError[`/attributes/${field.key}`])}
          onChange={(v) => setValues((current) => ({ ...current, [field.key]: v }))}
        />
      ))}

      {orphaned.length > 0 && attributesChanged && (
        <p className="field-error" style={{ margin: 0 }}>
          {orphaned.join(", ")} {orphaned.length === 1 ? "is" : "are"} stored on this contact under
          a field that no longer exists. Saving an attribute change will remove{" "}
          {orphaned.length === 1 ? "it" : "them"}. Recreate the field first to keep{" "}
          {orphaned.length === 1 ? "it" : "them"}.
        </p>
      )}

      {error && <p className="field-error">{error.message}</p>}

      <div className="row" style={{ gap: "var(--oc-space-2)" }}>
        <button type="submit" className="oc-btn oc-btn--sm" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** One input, shaped by the field's declared type. */
function AttributeInput({
  field,
  value,
  invalid,
  onChange,
}: {
  field: CustomField;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const label = <span className="oc-body-sm oc-text-secondary">{field.label}</span>;

  if (field.type === "enum") {
    return (
      <label className="login__field">
        {label}
        <select
          className="oc-input oc-input--sm"
          value={value}
          aria-invalid={invalid}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Not set</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === "boolean") {
    // A select rather than a checkbox, because a checkbox cannot express "not
    // set" — and unset is different from false for a field somebody filters on.
    return (
      <label className="login__field">
        {label}
        <select
          className="oc-input oc-input--sm"
          value={value}
          aria-invalid={invalid}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Not set</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
    );
  }

  return (
    <label className="login__field">
      {label}
      <input
        className="oc-input oc-input--sm"
        type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
        value={value}
        aria-invalid={invalid}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/**
 * Turns the form's string into the JSON type the field's definition requires.
 *
 * `checkType` on the server asserts the actual JSON type — a number field is
 * refused a string — so sending everything as text would 422 on three of the
 * five types.
 */
function coerce(field: CustomField, raw: string): unknown {
  switch (field.type) {
    case "number": {
      const n = Number(raw);
      // Left as a string when it is not a number, so the server's own message
      // arrives rather than NaN being encoded as null and clearing the value.
      return Number.isFinite(n) ? n : raw;
    }
    case "boolean":
      return raw === "true";
    default:
      // text, date and enum are all strings on the wire. The server parses a
      // date as YYYY-MM-DD or RFC 3339, and <input type="date"> emits the first.
      return raw;
  }
}

/**
 * Tags, which until now could be filtered on and never applied.
 *
 * `POST /contacts/{id}/tags` and its delete had no caller anywhere, so a tag
 * could only arrive through a CSV import — while Contacts filters by tag and
 * the campaign composer narrows an audience by one. This is the fourth time
 * that shape has shipped here, after Groups, Teams and Providers, and the
 * comment on GroupsSection below used to say so: "the first editable taxonomy
 * control in this drawer — Tags above is display."
 *
 * TAGGING CREATES BY NAME, which is why this takes a text input and not only a
 * picker. There is no POST /v1/tags — a tag exists because somebody applied it,
 * and `GET /v1/tags` returns what is in use with a count. So the control offers
 * the existing ones and accepts a new name, and the difference between the two
 * is invisible to the person using it, which is correct.
 */
function TagsSection({
  contact,
  editable,
  onChanged,
}: {
  contact: Contact;
  editable: boolean;
  onChanged: () => void;
}) {
  const { orgId } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);

  const all = useAsync<ContactTag[]>(
    (signal) => api.get<ContactTag[]>("/v1/tags", { signal }),
    [orgId],
  );

  const current = contact.tags ?? [];
  const currentNames = new Set(current.map((t) => t.name.toLowerCase()));
  const available = (all.data ?? []).filter((t) => !currentNames.has(t.name.toLowerCase()));

  const add = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setBusy(trimmed);
    setError(null);
    try {
      await api.post(`/v1/contacts/${encodeURIComponent(contact.id)}/tags`, { name: trimmed });
      setAdding("");
      all.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not apply the tag.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (tagId: string) => {
    setBusy(tagId);
    setError(null);
    try {
      await api.delete(
        `/v1/contacts/${encodeURIComponent(contact.id)}/tags/${encodeURIComponent(tagId)}`,
      );
      all.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the tag.");
    } finally {
      setBusy(null);
    }
  };

  if (!editable && current.length === 0) return null;

  return (
    <section className="drawer-section">
      <span className="oc-label">Tags</span>

      {current.length === 0 && (
        <span className="oc-body-sm oc-text-muted-aa">
          No tags. A campaign audience can be narrowed to a tag, and the contact list can be
          filtered by one.
        </span>
      )}

      {current.length > 0 && (
        <div className="row" style={{ flexWrap: "wrap", gap: "var(--oc-space-2)" }}>
          {current.map((tag) => (
            <span className="oc-chip oc-chip--static" key={tag.id}>
              {tag.name}
              {editable && (
                <button
                  type="button"
                  className="oc-chip__remove"
                  aria-label={`Remove the ${tag.name} tag`}
                  disabled={busy === tag.id}
                  onClick={() => void remove(tag.id)}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {editable && (
        <div className="row" style={{ gap: "var(--oc-space-2)" }}>
          <input
            className="oc-input oc-input--sm grow"
            list="contact-tag-options"
            placeholder="Add a tag…"
            value={adding}
            disabled={busy !== null}
            onChange={(event) => setAdding(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void add(adding);
              }
            }}
          />
          {/* A datalist rather than a select: it suggests what exists without
              refusing a name that does not, which is what tagging by name
              needs. */}
          <datalist id="contact-tag-options">
            {available.map((tag) => (
              <option key={tag.id} value={tag.name} />
            ))}
          </datalist>
          <button
            type="button"
            className="oc-btn oc-btn--sm"
            disabled={busy !== null || !adding.trim()}
            onClick={() => void add(adding)}
          >
            Add
          </button>
        </div>
      )}

      {error && <p className="field-error">{error}</p>}
    </section>
  );
}

/**
 * Removal and erasure, side by side and worded so they cannot be confused.
 *
 * They look similar and are not: one is undone by clearing a timestamp, the
 * other reaches across the message log and cannot be undone at all.
 */
/**
 * The other half of GDPR, which had no button at all.
 *
 * Erasure — Article 17 — has been wired since the audience work, behind a typed
 * ERASE confirmation. Article 15, the subject access request, is the one that
 * actually arrives regularly, and `GET /contacts/{id}/export` had no caller
 * anywhere in the dashboard.
 *
 * ITS OWN SECTION, AND ITS OWN CAPABILITY. Not in the Removal panel below,
 * which returns null without `contacts:write` — an Analyst holds
 * `contacts:export` and not `contacts:write`, and they are exactly the person
 * likely to be handling one of these. Gating an export on a write capability
 * would hide it from the only role defined to have it, which is the mistake the
 * attention strip made once already by gating on `domains:read` where the
 * screen needed `domains:manage`.
 *
 * Through `api.download`, not an `<a href>`. The bulk export on the Contacts
 * screen was a link and had been answering 401 for months — a plain navigation
 * carries no `X-OpenComms-Org`, which is the only thing the session middleware
 * reads. Copying that pattern here is how this was found.
 */
function SubjectAccess({ contact }: { contact: Contact }) {
  const { can } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!can("contacts:export")) return null;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await download(
        `/v1/contacts/${encodeURIComponent(contact.id)}/export`,
        `contact-${contact.id}.json`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not build the export.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="drawer-section">
      <span className="oc-label">Data request</span>
      <div className="oc-panel" style={{ background: "var(--oc-bg-inset)" }}>
        <div className="oc-panel__body row row--between" style={{ gap: "var(--oc-space-3)" }}>
          <div className="grow">
            <div className="oc-body-sm" style={{ fontWeight: 500 }}>
              Download everything held about this person
            </div>
            <div className="oc-body-sm oc-text-secondary">
              Every address, its consent history and source, tags, groups and attributes — what a
              subject access request asks for.
            </div>
          </div>
          <button
            type="button"
            className="oc-btn oc-btn--sm"
            disabled={busy}
            onClick={() => void run()}
          >
            {busy ? "Preparing…" : "Download"}
          </button>
        </div>
      </div>
      {error && <p className="field-error">{error}</p>}
    </section>
  );
}

function DangerZone({
  contact,
  onErased,
  onClose,
}: {
  contact: Contact;
  onErased: () => void;
  onClose: () => void;
}) {
  const { can } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!can("contacts:write")) return null;

  const remove = async () => {
    if (!window.confirm(`Remove ${contact.name || "this contact"} from the audience? This can be undone.`)) return;

    setBusy(true);
    try {
      await api.delete(`/v1/contacts/${encodeURIComponent(contact.id)}`);
      onErased();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the contact.");
      setBusy(false);
    }
  };

  const erase = async () => {
    const confirmation = window.prompt(
      `Erasure is permanent. It destroys this contact, every address, the consent history, and removes ` +
        `the recipient address and rendered body from every message sent to them.\n\n` +
        `Delivery counts survive with no address attached.\n\n` +
        `Type ERASE to continue.`,
    );
    if (confirmation !== "ERASE") return;

    setBusy(true);
    try {
      const receipt = await api.post<{
        identities_erased: number;
        messages_redacted: number;
        consent_events_erased: number;
      }>(`/v1/contacts/${encodeURIComponent(contact.id)}/erase`);

      window.alert(
        `Erased.\n\n` +
          `${receipt.identities_erased} addresses removed\n` +
          `${receipt.messages_redacted} messages redacted\n` +
          `${receipt.consent_events_erased} consent records destroyed`,
      );
      onErased();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not erase the contact.");
      setBusy(false);
    }
  };

  return (
    <section className="drawer-section">
      <span className="oc-label">Removal</span>
      {error && <p className="field-error">{error}</p>}

      <div className="oc-panel" style={{ background: "var(--oc-bg-inset)" }}>
        <div className="oc-panel__body row row--between" style={{ gap: "var(--oc-space-3)" }}>
          <div className="grow">
            <div className="oc-body-sm" style={{ fontWeight: 500 }}>
              Remove from the audience
            </div>
            <div className="oc-body-sm oc-text-secondary">
              Hidden from lists and skipped by campaigns. Reversible.
            </div>
          </div>
          <button type="button" className="oc-btn oc-btn--sm" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
        </div>
      </div>

      {can("contacts:erase") && (
        <div className="oc-panel oc-panel--critical" style={{ background: "var(--oc-bg-inset)" }}>
          <div className="oc-panel__body row row--between" style={{ gap: "var(--oc-space-3)" }}>
            <div className="grow">
              <div className="oc-body-sm" style={{ fontWeight: 500 }}>
                Erase permanently
              </div>
              <div className="oc-body-sm oc-text-secondary">
                The GDPR erasure request. Destroys the record and redacts the mail trail. Cannot be
                undone.
              </div>
            </div>
            <button
              type="button"
              className="oc-btn oc-btn--danger oc-btn--sm"
              disabled={busy}
              onClick={() => void erase()}
            >
              Erase
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Which groups this contact is in, and a way to change it.
 *
 * The first editable taxonomy control in this drawer — Tags above is display
 * only. Groups get one because a group is what a campaign is sent to, so
 * "put this person on the newsletter list" is a thing someone does while
 * looking at the person, not while looking at the list.
 *
 * Rendered whether or not the contact is in any group, unlike Tags, which
 * hides when empty. An empty tag list is nothing to act on; an empty group
 * list is the state someone came here to change.
 */
function GroupsSection({
  contact,
  editable,
  onChanged,
}: {
  contact: Contact;
  editable: boolean;
  onChanged: () => void;
}) {
  const { orgId } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const all = useAsync<CursorPage<Group>>(
    (signal) => api.get<CursorPage<Group>>("/v1/groups", { query: { limit: 100 }, signal }),
    [orgId],
  );

  const current = contact.groups ?? [];
  const currentIds = new Set(current.map((g) => g.id));
  const available = (all.data?.data ?? []).filter((g) => !currentIds.has(g.id));

  const add = async (groupId: string) => {
    setBusy(groupId);
    setError(null);
    try {
      await api.post(`/v1/groups/${encodeURIComponent(groupId)}/members`, {
        contact_ids: [contact.id],
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add to the group.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (groupId: string) => {
    setBusy(groupId);
    setError(null);
    try {
      await api.delete(
        `/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(contact.id)}`,
      );
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove from the group.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="drawer-section">
      <span className="oc-label">Groups</span>

      {current.length === 0 && (
        <span className="oc-body-sm oc-text-muted-aa">
          Not in any group. A campaign can be sent to a group, so this is how someone
          starts receiving one.
        </span>
      )}

      {current.length > 0 && (
        <div className="row" style={{ flexWrap: "wrap", gap: "var(--oc-space-2)" }}>
          {current.map((group) => (
            <span className="oc-chip oc-chip--static" key={group.id}>
              {group.name}
              {editable && (
                <button
                  type="button"
                  className="oc-chip__remove"
                  aria-label={`Remove from ${group.name}`}
                  disabled={busy === group.id}
                  onClick={() => void remove(group.id)}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {editable && available.length > 0 && (
        <select
          className="oc-input"
          aria-label="Add to a group"
          value=""
          disabled={busy !== null}
          onChange={(event) => {
            if (event.target.value) void add(event.target.value);
          }}
        >
          <option value="">Add to a group…</option>
          {available.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.member_count})
            </option>
          ))}
        </select>
      )}

      {editable && all.data && available.length === 0 && current.length === 0 && (
        <span className="oc-body-sm oc-text-muted-aa">
          No groups exist yet. Create one on the Groups screen.
        </span>
      )}

      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </section>
  );
}
